import { useCallback, useEffect, useState } from 'react';
import { Plus, Lock, Pencil, Copy, Trash2, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { dialog } from '../../store/dialogStore';
import { isOrgAdmin, hasCapability } from '../../utils/permissions';
import { fetchRoles, fetchCapabilities, createRole, updateRole, deleteRole } from '../../api/access';
import { roleColor, isOwnerRole, levelLabel, stripOwnerOnlyCapabilities } from '../../components/access/constants';
import RoleFormModal from '../../components/access/RoleFormModal';

function scopeLabel(role) {
  if (role.scope_mode === 'all_workspaces') return 'Todas as áreas';
  if (role.scope_mode === 'none') return 'Nenhuma área';
  const n = role.workspace_ids?.length ?? 0;
  return `${n} ${n === 1 ? 'área' : 'áreas'}`;
}

// Editor de grupos de permissão (Configurações → Grupos), arquitetura-v2.md §6.13.
export default function RolesPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const canManage = isOrgAdmin(currentUser) || hasCapability(currentUser, 'org.roles.manage');

  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRole, setEditingRole] = useState(undefined); // undefined = fechado; null = novo; objeto = editar

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchRoles()
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message || 'Falha ao carregar grupos'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchCapabilities().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const handleSubmit = async (payload) => {
    if (editingRole) {
      await updateRole(editingRole.id, payload);
    } else {
      await createRole(payload);
    }
    load();
  };

  const handleDuplicate = async (role) => {
    try {
      await createRole({
        name: `${role.name} (cópia)`,
        default_level: role.default_level,
        scope_mode: role.scope_mode,
        workspace_ids: role.workspace_ids || [],
        // org.reset / org.transfer_ownership são exclusivas do Proprietário —
        // se o grupo original as tiver (dado legado), duplicar não as propaga.
        capabilities: stripOwnerOnlyCapabilities(role.capabilities),
      });
      load();
    } catch (err) {
      const msg = err.status === 403
        ? (err.message || 'Permissão negada: alguma capability do grupo original é exclusiva do Proprietário.')
        : err.message;
      dialog.alert('Erro ao duplicar grupo', msg);
    }
  };

  const handleDeleteFromList = async (role) => {
    const ok = await dialog.confirm(
      `Excluir grupo "${role.name}"`,
      'Esta ação não pode ser desfeita.',
      { confirmLabel: 'Excluir', danger: true }
    );
    if (!ok) return;
    try {
      await deleteRole(role.id);
      load();
    } catch (err) {
      const msg = err.status === 409
        ? (err.message || 'Este grupo não pode ser excluído: ele tem pessoas atribuídas ou é um grupo de sistema.')
        : (err.message || 'Falha ao excluir grupo');
      dialog.alert('Não foi possível excluir', msg);
    }
  };

  const handleDeleteFromModal = async (role) => {
    try {
      await deleteRole(role.id);
      load();
    } catch (err) {
      const msg = err.status === 409
        ? (err.message || 'Este grupo não pode ser excluído: ele tem pessoas atribuídas ou é um grupo de sistema.')
        : (err.message || 'Falha ao excluir grupo');
      throw new Error(msg);
    }
  };

  if (!canManage) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <p className="text-sm text-text-secondary">Você não tem permissão para gerir grupos de permissão.</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/settings/users')}
        className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary mb-4 transition-colors"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Voltar para usuários
      </button>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-text-primary">Grupos de permissão</h2>
        <button
          onClick={() => setEditingRole(null)}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/80 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Novo grupo
        </button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/[0.02]">
            <tr className="text-left text-xs text-text-secondary border-b border-border/50">
              <th className="px-4 py-3 font-medium">Grupo</th>
              <th className="px-4 py-3 font-medium">Nível padrão</th>
              <th className="px-4 py-3 font-medium">Escopo</th>
              <th className="px-4 py-3 font-medium">Pessoas</th>
              <th className="px-4 py-3 font-medium w-28"></th>
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-danger">{error} · <button onClick={load} className="underline">Tentar de novo</button></td></tr>
            )}
            {!error && loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-text-secondary">Carregando…</td></tr>
            )}
            {!error && !loading && roles.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-text-secondary">Nenhum grupo encontrado.</td></tr>
            )}
            {!error && !loading && roles.map((role) => {
              const locked = isOwnerRole(role);
              return (
                <tr key={role.id} className="border-b border-border/30 hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setEditingRole(role)}
                      className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: roleColor(role) }} />
                      <span className="text-sm text-text-primary">{role.name}</span>
                      {locked && <Lock className="w-3.5 h-3.5 text-text-secondary" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{levelLabel(role.default_level)}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{scopeLabel(role)}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{role.user_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingRole(role)}
                        title="Editar"
                        className="p-1.5 rounded hover:bg-white/10 text-text-secondary hover:text-text-primary transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(role)}
                        title="Duplicar grupo"
                        className="p-1.5 rounded hover:bg-white/10 text-text-secondary hover:text-text-primary transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      {!locked && (
                        <button
                          onClick={() => handleDeleteFromList(role)}
                          title="Excluir"
                          className="p-1.5 rounded hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingRole !== undefined && (
        <RoleFormModal
          role={editingRole}
          capabilitiesCatalog={catalog}
          onClose={() => setEditingRole(undefined)}
          onSubmit={handleSubmit}
          onDelete={editingRole ? handleDeleteFromModal : undefined}
        />
      )}
    </div>
  );
}
